<template>
  <div>
    <multiselect
      :id="id"
      :ref="id"
      v-model="value"
      :placeholder="placeholder"
      label="name"
      :custom-label="customLabel"
      :track-by="track"
      :disabled="disabled"
      :options="options"
      :option-height="30"
      :show-labels="false"
      :multiple="multiple"
      :limit="limit"
      :limit-text="more"
      :max="max"
      :openDirection="openDirection"
      :max-height="300"
      :close-on-select="!multiple"
      :searchable="searchable"
      :internal-search="false"
      @open="repositionDropdown"
      @input="changed"
      @search-change="searchChanged"
      :class="{ invalid: validation && !isValid, valid: validation && isValid }"
    >
      <template slot="option" slot-scope="props">
        <div class="wrapped-option">
          <!-- --------------------------------------------------------------------------------------------------------------- -->

          <div>
            <span v-if="customIcon">
              <i :class="customIcon"></i>
            </span>

            <span v-if="!customIcon && props.option.scope">
              <i v-if="props.option.scope.value === 'system'" class=" mdi mdi-database"></i>
              <i v-if="props.option.scope.value === 'workspace'" class=" mdi mdi-hexagon-multiple-outline"></i>

              {{ customLabel ? customLabel(props.option) : props.option.name | translate }}
            </span>

            <span v-else-if="props.option.organization">
              <i class="bx bxs-school"></i>
              {{ customLabel ? customLabel(props.option) : props.option.name | translate }}
            </span>

            <span v-else>
              {{ customLabel ? customLabel(props.option) : props.option.name | translate }}
            </span>
          </div>

          <!-- --------------------------------------------------------------------------------------------------------------- -->
          <span v-if="props.option.fileName" class="pl-3" v-b-tooltip.hover :title="$t('file-manager.name')">
            <i class="bx bx-layer"></i>
            {{ props.option.fileName | translate }}
          </span>

          <span v-if="props.option.category" class="pl-3" v-b-tooltip.hover :title="$t('common.category')">
            <i class="bx bx-layer"></i>
            {{ props.option.category.name | translate }}
          </span>
          <span v-if="props.option.parent" class="pl-3" v-b-tooltip.hover :title="$t('common.paren')">
            <i class="bx bx-layer"></i>

            {{ getCategoryPath(props.option) }};
          </span>
          <!-- --------------------------------------------------------------------------------------------------------------- -->

          <span v-if="props.option.department" class="pl-3" v-b-tooltip.hover :title="$t('department.text')">
            <i class="bx bx-sitemap"></i>
            {{ props.option.department.name | translate }}
          </span>

          <!-- --------------------------------------------------------------------------------------------------------------- -->

          <span v-if="props.option.description" class="pl-3" v-b-tooltip.hover :title="$t('common.description')">
            <i class="bx bx-spreadsheet"></i>
            {{ props.option.description | translate }}
          </span>

          <!-- --------------------------------------------------------------------------------------------------------------- -->
          <span v-if="currentProduct == 'GDPR' && props.option.gdprRegulation" class="pl-3" v-b-tooltip.hover :title="$t('common.regulation-gdpr')">
            <i class="bx bx-spreadsheet"></i>
            {{ props.option.gdprRegulation | translate }}
          </span>
          <span v-if="currentProduct == 'KVKK' && props.option.kvkkRegulation" class="pl-3" v-b-tooltip.hover :title="$t('common.regulation-kvkk')">
            <i class="bx bx-spreadsheet"></i>
            {{ props.option.kvkkRegulation | translate }}
          </span>
          <span v-if="!hideVerbisAquivalent" class="pl-3" v-b-tooltip.hover :title="$t('common.aquivalent-verbis')">
            <span v-if="props.option.verbisAquivalent">
              <i class="bx bx-spreadsheet"></i>
              {{ props.option.verbisAquivalent.name | translate }}
            </span>
            <span v-if="props.option.isVerbisPurpose && !hideVerbisAquivalent">
              <i class="bx bx-spreadsheet"></i>
              {{ props.option.name | translate }}
            </span>
          </span>

          <!-- --------------------------------------------------------------------------------------------------------------- -->

          <span v-if="props.option.law" v-b-tooltip.hover :title="$t('property.other.law')">
            <i class="bx bx-spreadsheet"></i>
            {{ props.option.law | translate }}
          </span>
        </div>
      </template>

      <template slot="selection" slot-scope="{ values, remove }">
        <span v-for="option in values" :key="'filter_option' + option._id" class="wrapped-option badge badge-primary mr-1 p-2">
          <span>
            {{ customLabel ? customLabel(option) : option.name | translate }}
          </span>
          <span v-if="option.category">
            <br />
            <i class="bx bx-sitemap"></i>
            {{ option.category.name | translate }}
          </span>
          <span v-if="option.description">
            <br />
            <i class="bx bx-spreadsheet"></i>
            {{ option.description | translate }}
          </span>

          <span v-if="currentProduct == 'GDPR' && option.gdprRegulation" class="pl-3" v-b-tooltip.hover :title="$t('common.regulation-gdpr')">
            <i class="bx bx-spreadsheet"></i>
            {{ option.gdprRegulation | translate }}
          </span>
          <span v-if="currentProduct == 'KVKK' && option.kvkkRegulation" class="pl-3" v-b-tooltip.hover :title="$t('common.regulation-kvkk')">
            <i class="bx bx-spreadsheet"></i>
            {{ option.kvkkRegulation | translate }}
          </span>

          <span v-if="!hideVerbisAquivalent" class="" v-b-tooltip.hover :title="$t('common.aquivalent-verbis')">
            <span v-if="!option.isVerbisPurpose && option.verbisAquivalent">
              <br />
              <i class="bx bx-spreadsheet"></i>
              {{ option.verbisAquivalent.name | translate }}
            </span>

            <span v-if="option.isVerbisPurpose">
              <br />
              {{ option.name | translate }}
            </span>
          </span>

          <span v-if="option.law">
            <br />
            <i class="bx bx-spreadsheet"></i>
            {{ $t("property.other.law") }} :
            {{ option.law | translate }}
          </span>

          <i class="fa fa-times ml-1 hand text-danger" @click="remove(option)"></i>
        </span>

        <span v-if="selected && limit && selected.length > limit" class="wrapped-option badge badge-primary mr-1 p-2">(+{{ selected.length - limit }} more) </span>
      </template>

      <template slot="caret" v-if="validation && !isValid">
        <div>
          <div class="multiselect__icon">
            <i class="mdi mdi-close-thick multiselect__invalid-icon"></i>
          </div>
        </div>
      </template>
      <template slot="caret" v-if="validation && isValid">
        <div>
          <div class="multiselect__icon">
            <i class="mdi mdi-check-bold multiselect__valid-icon"></i>
          </div>
        </div>
      </template>

      <slot slot="noResult">{{ $t("common.not-found") }}</slot>
      <slot slot="noOptions">{{ $t("common.not-found") }}</slot>

      <template slot="singleLabel" slot-scope="{ option }">
        <span v-if="option.scope">
          <span v-if="option.scope.value === 'workspace'">
            <i class="text-muted mdi mdi-hexagon-multiple-outline"></i>
            <strong>{{ option.name | translate }}</strong>
          </span>
          <span v-else-if="option.scope.value === 'system'">
            <i class="text-muted mdi mdi-database"></i>
            <strong>{{ option.name | translate }}</strong>
          </span>
          <span v-else="option.scope.value === 'system'">
            <i class="text-muted mdi mdi-database"></i>
            <strong>{{ option.name | translate }}</strong>
          </span>
        </span>
        <span v-else>
          <i v-if="option.organization" class="text-muted bx bxs-school align-middle"></i>
          <strong>{{ option.name | translate }}</strong>
        </span>
      </template>

      <template v-if="customLabel" slot="tag" slot-scope="{ option }">
        <span class="multiselect__tag">{{ customLabel(option) | translate }} <i aria-hidden="true" @click="remove(option)" tabindex="1" class="multiselect__tag-icon"></i></span>
      </template>

      <slot slot="beforeList">
        <div class="p-2">
          <a @click="reset" href="javascript:;">
            <i class="fa fa-eraser multiselect__control"></i>
            <span class="multiselect__control"> {{ $t("common.clear-selection") }}</span>
          </a>
        </div>
      </slot>

      <slot slot="afterList">
        <div class="p-2 ">
          <a @click="reset" href="javascript:;">
            <i class="fa fa-eraser multiselect__control"></i>
            <span class="multiselect__control"> {{ $t("common.clear-selection") }}</span>
          </a>
        </div>
      </slot>
      <slot slot="beforeList" v-if="multiple && value && value.length < max">
        <div class="p-2">
          <a @click="selectAll" href="javascript:;">
            <i class="bx bx-select-multiple multiselect__control"></i>
            <span class="multiselect__control">{{ $t("common.select-all") }}</span>
          </a>
        </div>
      </slot>

      <slot slot="maxElements">
        <div class="p-2">
          <span>{{ $t("common.selection-limit", { max: max }) }}</span>
        </div>
      </slot>
    </multiselect>
  </div>
</template>

<script>
function uuid() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function(c) {
    var r = (Math.random() * 16) | 0,
      v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
export default {
  props: {
    customIcon: {
      type: String,
      required: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    track: {
      type: String,
      default: "_id",
    },
    // Accepts 'top' / 'above' to always open above, 'bottom' / 'below'
    openDirection: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
    },
    selected: {
      type: [Object, Array],
    },
    searchable: {
      type: Boolean,
      default: true,
    },
    customLabel: {
      type: Function,
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },

    limit: {
      default: 10,
      type: Number,
      required: false,
    },
    validation: {
      type: Boolean,
      required: false,
    },
    isValid: {
      type: Boolean,
      required: false,
    },
    customLabel: {
      type: Function,
      required: false,
    },
    max: {
      type: Number,
      required: false,
    },
    hideVerbisAquivalent: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      value: undefined,
      id: uuid(),
      options: [],
    };
  },
  mounted() {
    this.searchChanged("*");
    if (this.selected) {
      this.changed(this.selected);
    }
  },
  watch: {
    selected(selected) {
      this.value = selected;
    },
  },

  methods: {
    more(count) {
      return this.$t("common.multi-select", { count });
    },
    getCategoryPath(category) {
      if (!category) {
        return;
      }
      if (!category.parent) {
        return category.name[this.currentLanguage];
      }

      return this.getCategoryPath(category.parent) + " > " + category.name[this.currentLanguage];
    },
    selectAll() {
      if (this.max) {
        this.value = this.options.slice(0, this.max);
      } else {
        this.value = this.options;
      }
      this.$refs[this.id].toggle();
      this.changed(this.value);
    },
    reset() {
      this.value = undefined;
      this.searchChanged("*");
      this.changed(undefined);
    },
    async searchChanged(value) {
      value = value == "*" ? "" : value;
      const self = this;
      this.$emit("filter", value.trim(), (data) => {
        self.options = [...data];
      });
    },
    remove(item) {
      if (Array.isArray(this.value)) {
        const index = this.value.indexOf(item);
        this.value.splice(index, 1);
      }
    },
    changed(values) {
      let result = undefined;
      if (values) {
        if (Array.isArray(values)) {
          result = [...values];
        } else {
          result = { ...values };
        }
      }

      this.value = result; //! Probleme sebep olabilir

      this.$emit("changed", result ? result : this.multiple ? [] : result);
      this.$forceUpdate();
    },

    repositionDropdown(id) {
      if (this.searchable) {
        const el = document.getElementById(id);
        const parent = el.closest(".multiselect");
        if (!["td", "tr", "th"].includes(parent.parentNode.nodeName.toLowerCase())) {
          return;
        }
        const content = parent.querySelector(".multiselect__content-wrapper");
        // const { top, height, left } = parent.getBoundingClientRect();
        content.style.width = `${parent.clientWidth}px`;
        content.style.position = "fixed";
        content.style.bottom = "auto";
      }
    },
  },
};
</script>
<style scoped>
i.bx,
i.mdi {
  margin-right: 3px !important;
}
.wrapped-option {
  max-width: 100%;
  text-align: left;
  text-overflow: ellipsis;
  white-space: wrap; /* Don't forget this one */
  line-height: 14px;
}
.multiselect--active .multiselect__content {
  z-index: 999999999; /* Sadece açıldığında z-index değeri yükseltilir */
}
</style>
